/**
 * The cosmetic half of an XP award. The caller owns and commits the balance;
 * this class only presents that already-committed value.
 */
export function cleanHousePraise(line, fallback = "Nicely done.") {
  let clean = String(line == null ? "" : line).trim();
  if (!clean) return fallback;
  clean = clean.replace(/\bbrilliant\b/gi, "excellent");
  clean = clean.replace(/!+/g, ".");
  clean = clean.replace(/\s+/g, " ").replace(/\.\s*\./g, ".");
  return clean || fallback;
}

export class RewardMotion {
  constructor({
    duration = 600,
    requestFrame,
    cancelFrame,
    now,
    reducedMotion = false,
    quiet = false,
    onStart,
    onFinish,
  } = {}) {
    this.duration = duration;
    this._raf = requestFrame || ((fn) => requestAnimationFrame(fn));
    this._caf = cancelFrame || ((id) => cancelAnimationFrame(id));
    this._now = now || (() => (
      typeof performance !== "undefined" ? performance.now() : Date.now()
    ));
    this.reducedMotion = !!reducedMotion;
    this.quiet = !!quiet;
    this.onStart = onStart;
    this.onFinish = onFinish;
    this._frame = null;
    this._animation = null;
    this._chip = null;
    this._generation = 0;
    this._number = null;
    this._actual = 0;
  }

  count({ from = 0, to = 0, number } = {}) {
    this.cancel(to);
    const generation = this._generation;
    this._number = number || null;
    this._actual = to | 0;

    if (!this._number) return;
    if (this.reducedMotion || this.duration <= 0) {
      this._number.textContent = String(this._actual);
      return;
    }

    this._number.textContent = String(from | 0);
    if (!this.quiet && typeof this.onStart === "function") this.onStart();
    const started = this._now();
    const tick = (time) => {
      if (generation !== this._generation) return;
      const elapsed = Math.max(0, Number(time) - started);
      const progress = Math.min(1, elapsed / this.duration);
      this._number.textContent = String(Math.round(from + (to - from) * progress));
      if (progress < 1) {
        this._frame = this._raf(tick);
        return;
      }
      this._frame = null;
      this._number.textContent = String(this._actual);
      if (!this.quiet && typeof this.onFinish === "function") this.onFinish();
    };
    this._frame = this._raf(tick);
  }

  fly({ source, target, text = "" } = {}) {
    if (this.reducedMotion || !source || !target || typeof document === "undefined") return;
    const from = source.getBoundingClientRect();
    const to = target.getBoundingClientRect();
    if (!from.width || !from.height || !to.width || !to.height) return;

    this._removeChip();
    const chip = document.createElement("span");
    chip.className = "ts-reward-chip";
    chip.textContent = text;
    Object.assign(chip.style, {
      position: "fixed",
      left: `${from.left}px`,
      top: `${from.top}px`,
      width: `${from.width}px`,
      height: `${from.height}px`,
      zIndex: "10000",
      pointerEvents: "none",
      display: "grid",
      placeItems: "center",
    });
    document.body.appendChild(chip);
    this._chip = chip;

    if (typeof chip.animate !== "function") {
      this._removeChip();
      return;
    }
    const dx = to.left + to.width / 2 - (from.left + from.width / 2);
    const dy = to.top + to.height / 2 - (from.top + from.height / 2);
    const animation = chip.animate([
      { transform: "translate(0, 0) scale(1)", opacity: 1 },
      { transform: `translate(${dx}px, ${dy}px) scale(.72)`, opacity: 0.15 },
    ], {
      duration: this.duration,
      easing: "cubic-bezier(.2,.8,.2,1)",
      fill: "forwards",
    });
    this._animation = animation;
    const finish = () => {
      if (this._animation === animation) {
        this._animation = null;
        this._removeChip();
      }
    };
    animation.onfinish = finish;
    animation.oncancel = finish;
  }

  cancel(actual = this._actual) {
    this._generation += 1;
    if (this._frame != null) {
      this._caf(this._frame);
      this._frame = null;
    }
    if (this._animation) {
      const animation = this._animation;
      this._animation = null;
      try { animation.cancel(); } catch (_) {}
    }
    this._removeChip();
    this._actual = actual | 0;
    if (this._number) this._number.textContent = String(this._actual);
    this._number = null;
  }

  _removeChip() {
    if (this._chip && this._chip.parentNode) this._chip.remove();
    this._chip = null;
  }
}

export default RewardMotion;